import React, { useState, useEffect, useMemo } from 'react';
import { useMoney } from '../hooks/useMoney';
import { supplierPrice } from '../utils/supplierPrice';
import { useExport } from './header/exporters';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import { fmtId } from '../utils/jiraId';
import Icon from './Icon';
import EmptyState from './EmptyState';
import DataTable, { Column } from './DataTable';
import { MetricCard, MetricTone } from './MetricCards';
import { toggleCardFilter } from '../utils/cardFilter';
import type { ArchiveView } from '../utils/archiveFilter';
import { useSettings } from '../contexts/SettingsContext';
import '../styles/components/ProductCatalog.css';
import {
  Product,
  ProductType,
} from '../types/Product';
import {
  getProducts,
  refreshProductsFromApi,
  fetchArchivedProducts,
  getPreferredSupplier,
  getProductPrice,
  PRODUCT_CHANGE_EVENT,
} from '../services/ProductService';

// 'favour' and 'fabric'/'asoebi' (FabricCatalog, via its own
// /asoebi/fabrics endpoint) both ultimately read/write this same `products`
// table server-side — fabric rows are just products with types @> ['fabric'],
// filtered/re-shaped by ans-backend's asoebi repository layer. FabricCatalog
// keeps a separate frontend UI (not CatalogItemFormPage) because guest
// assignments and the reserved/paid stock stats are asoebi-specific concepts
// with no equivalent for favours/decor — not because the data is unmerged.
// 'decor', 'accessories', 'internal' exist in the schema but nothing creates
// them yet — Decor and Stationery are still separate tables (/decor,
// /stationery), not part of this consolidation. Listed anyway so the filter
// is ready the moment something starts using them.
const TYPE_LABELS: Record<ProductType, string> = {
  favour: 'Favour',
  asoebi: 'Asoebi',
  fabric: 'Fabric',
  decor: 'Decor',
  accessories: 'Accessories',
  internal: 'Internal',
};

const TYPE_CARDS: { type: ProductType; label: string; tone: MetricTone; icon: string }[] = [
  { type: 'favour', label: 'Favours', tone: 'pink', icon: 'heart' },
  { type: 'fabric', label: 'Fabric', tone: 'purple', icon: 'sparkles' },
  { type: 'asoebi', label: 'Asoebi', tone: 'orange', icon: 'sparkles' },
  { type: 'decor', label: 'Decor', tone: 'green', icon: 'grid' },
  { type: 'accessories', label: 'Accessories', tone: 'red', icon: 'grid' },
];

function totalStock(product: Product): number {
  return Object.values(product.stockPools).reduce((sum, v) => sum + (v ?? 0), 0);
}

const ProductCatalog: React.FC = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();
  const [products, setProducts] = useState<Product[]>(() => getProducts());
  const [archivedProducts, setArchivedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<ProductType | 'all'>('all');
  const [customizableOnly, setCustomizableOnly] = useState(false);
  // The Archived switch lives in Header's breadcrumb actions: off shows active
  // items, on shows archived ones.
  const [archiveFilter, setArchiveFilter] = useState<ArchiveView>('active');

  useEffect(() => {
    void refreshProductsFromApi().then((items) => {
      setProducts(items);
      setLoading(false);
    });
    const onChange = () => setProducts(getProducts());
    window.addEventListener(PRODUCT_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(PRODUCT_CHANGE_EVENT, onChange);
  }, []);

  // Only fetched when actually needed — most visits never touch the archived
  // view, so there is no reason to pay for it up front.
  useEffect(() => {
    if (archiveFilter === 'active') return;
    void fetchArchivedProducts().then(setArchivedProducts).catch(() => {});
  }, [archiveFilter]);

  // Lives in Header's breadcrumb alongside the type filter, same
  // sync/set CustomEvent convention as catalog-type-filter.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('catalog-archived-sync', { detail: archiveFilter }));
  }, [archiveFilter]);

  useEffect(() => {
    const onSet = (e: Event) => setArchiveFilter((e as CustomEvent).detail);
    window.addEventListener('catalog-archived-set', onSet);
    return () => window.removeEventListener('catalog-archived-set', onSet);
  }, []);

  const byArchiveStatus = useMemo(() => {
    return archiveFilter === 'archived' ? archivedProducts : products;
  }, [products, archivedProducts, archiveFilter]);

  const filtered = useMemo(
    () => byArchiveStatus.filter((p) =>
      (typeFilter === 'all' || p.types.includes(typeFilter)) && (!customizableOnly || p.customizable)),
    [byArchiveStatus, typeFilter, customizableOnly],
  );

  // The cards count the items in the chosen view (active or archived), so a
  // card keeps its number while it is the filter.
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: byArchiveStatus.length };
    for (const card of TYPE_CARDS) {
      counts[card.type] = byArchiveStatus.filter((p) => p.types.includes(card.type)).length;
    }
    return counts;
  }, [byArchiveStatus]);
  const customizableCount = useMemo(() => byArchiveStatus.filter((p) => p.customizable).length, [byArchiveStatus]);

  // Export button (Header): the items this list shows right now, in the
  // project's currency (blank prices when no currency is set yet).
  const money = useMoney();
  useExport('catalogue', {
    rows: filtered,
    ctx: {
      currency: money.currency,
      typeLabel: t => TYPE_LABELS[t as ProductType] ?? t,
      price: p => (money.currency ? String(getProductPrice(p as any, money.currency) ?? '') : ''),
      stock: p => totalStock(p as Product),
    },
  });

  const columns: Column<Product>[] = [
    idColumn<Product>('catalogueItem'),
    {
      key: 'name',
      header: 'Item',
      sortable: true,
      render: (_v, product) => (
        <div className="pc-item-cell">
          {product.image ? (
            <img src={product.image} alt={product.name} className="pc-item-thumb" />
          ) : (
            <div className="pc-item-thumb pc-item-thumb--empty"><Icon name="image" size={16} /></div>
          )}
          <span className="pc-item-name">{product.name}</span>
        </div>
      ),
    },
    {
      key: 'types',
      header: 'Type',
      render: (_v, product) => (
        <div className="pc-type-badges">
          {product.types.map((t) => (
            <span key={t} className="ds-badge ds-badge-neutral">{TYPE_LABELS[t] ?? t}</span>
          ))}
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      sortable: true,
      render: (_v, product) => {
        const supplier = getPreferredSupplier(product as any);
        if (!supplier) return <span className="pc-muted">—</span>;
        const price = supplierPrice(supplier, money.currency);
        return price == null ? <span className="pc-muted">—</span> : formatCurrency(price);
      },
    },
    {
      key: 'stock',
      header: 'Stock',
      align: 'right',
      sortable: true,
      render: (_v, product) => totalStock(product),
    },
    {
      key: 'suppliers',
      header: 'Suppliers',
      align: 'right',
      render: (_v, product) => product.suppliers.length,
    },
  ];

  return (
    <div className="wt-container fade-in">
      <section className="stats-grid">
        <MetricCard
          label="All Items"
          value={typeCounts.all}
          tone="blue"
          icon="grid"
          active={typeFilter === 'all'}
          onClick={() => setTypeFilter('all')}
          title="Show every type"
        />
        {TYPE_CARDS.map((card) => (
          <MetricCard
            key={card.type}
            label={card.label}
            value={typeCounts[card.type]}
            tone={card.tone}
            icon={card.icon}
            active={typeFilter === card.type}
            onClick={() => setTypeFilter(toggleCardFilter<ProductType | 'all'>(typeFilter, card.type, 'all'))}
            title={typeFilter === card.type ? `Showing ${card.label}. Click to clear.` : `Show only ${card.label}`}
          />
        ))}
        <MetricCard
          label="Customizable"
          value={customizableCount}
          tone="teal"
          icon="edit"
          active={customizableOnly}
          onClick={() => setCustomizableOnly((on) => !on)}
          title={customizableOnly ? 'Showing customizable items. Click to clear.' : 'Show only customizable items'}
        />
      </section>

      {!loading && filtered.length === 0 ? (
        <EmptyState
          icon="grid"
          title={
            archiveFilter === 'archived'
              ? 'No archived items'
              : typeFilter === 'all' ? 'No catalogue items yet' : 'No items of this type'
          }
          description={
            archiveFilter === 'archived'
              ? 'Items appear here once archived from the catalogue.'
              : 'Items added here (favours, fabric, asoebi) become available across the app wherever that item type is used.'
          }
          action={archiveFilter === 'archived' ? undefined : <button className="primary-btn" onClick={() => navigate('/catalog/new')}>Add First Item</button>}
        />
      ) : (
        <DataTable
          recordList={{ kind: 'catalogueItem' }}
          data={filtered}
          columns={columns}
          loading={loading}
          pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
          onRowClick={(product) => navigate(`/catalog/${fmtId('CT', product.id)}`)}
        />
      )}
    </div>
  );
};

export default ProductCatalog;
